import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import {
  DOCUMENT_GENERATION_HISTORY_LABELS,
  DOCUMENT_GENERATION_HISTORY_PAGE_SIZE,
} from '~/modules/covenants/CovenantsDataApplicationForm/components/documentGenerationHistory/constants.ts';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { clientStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/styles.ts';
import type { TDocumentGenerationHistoryModalProps } from '~/modules/covenants/CovenantsDataApplicationForm/components/documentGenerationHistory/types.ts';
import {
  getHistoryItemDetails,
  getHistoryVersionLabel,
} from '~/modules/covenants/CovenantsDataApplicationForm/components/documentGenerationHistory/utils.ts';
import type { TApplicationDocumentHistoryResponse } from '~/modules/covenants/types.ts';
import { StateAidCertificateTypeSelector } from '~/modules/covenants/CovenantsDataApplicationForm/components/documentGenerationHistory/StateAidCertificateTypeSelector.tsx';

export const DocumentGenerationHistoryModal: React.FC<TDocumentGenerationHistoryModalProps> = ({
  open,
  documentName,
  onClose,
  onLoad,
  onSelect,
  onCreateNew,
  requiresStateAidType = false,
  stateAidCertificateType,
  onStateAidCertificateTypeChange,
  isRegenerationTypeSelection = false,
  onRegenerate,
}) => {
  const [page, setPage] = useState(0);
  const [history, setHistory] = useState<TApplicationDocumentHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  useEffect(() => {
    if (!open || isRegenerationTypeSelection) {
      setPage(0);
      setHistory(null);
      return;
    }

    setIsLoading(true);
    void onLoad(page, DOCUMENT_GENERATION_HISTORY_PAGE_SIZE)
      .then(setHistory)
      .finally(() => setIsLoading(false));
  }, [isRegenerationTypeSelection, onLoad, open, page]);

  const canProceed = !requiresStateAidType || Boolean(stateAidCertificateType);

  const handleSelect = async (id: number) => {
    const item = history?.content.find((candidate) => candidate.id === id);
    if (!item) {
      return;
    }

    setIsSelecting(true);
    try {
      await onSelect(item, stateAidCertificateType);
    } finally {
      setIsSelecting(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (isRegenerationTypeSelection && stateAidCertificateType) {
      setIsSelecting(true);
      try {
        await onRegenerate?.(stateAidCertificateType);
      } finally {
        setIsSelecting(false);
      }
      return;
    }

    onCreateNew(stateAidCertificateType);
  };

  return (
    <Dialog
      open={open}
      onClose={isSelecting ? undefined : onClose}
      slotProps={{ paper: { sx: clientStyles.createDialogPaper } }}
    >
      <DialogTitle sx={clientStyles.createTitle}>
        <Box sx={clientStyles.createHeaderContent}>
          <Typography component="span" sx={clientStyles.createTitleText}>
            {DOCUMENT_GENERATION_HISTORY_LABELS.title} «{documentName}»
          </Typography>
          <Typography variant="body2" sx={clientStyles.createSubtitle}>
            {DOCUMENT_GENERATION_HISTORY_LABELS.description}
          </Typography>
        </Box>
        <IconButton
          aria-label="Закрити"
          onClick={onClose}
          disabled={isSelecting}
          sx={clientStyles.createCloseButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={clientStyles.createContent}>
        <Box sx={[commonStyles.loadingWrap, clientStyles.createBody]}>
          <Divider sx={clientStyles.createDivider} />
          {requiresStateAidType && (
            <StateAidCertificateTypeSelector
              value={stateAidCertificateType}
              onChange={(type) => onStateAidCertificateTypeChange?.(type)}
            />
          )}
          {isRegenerationTypeSelection && (
            <Typography variant="body2" sx={clientStyles.createListDate}>
              {DOCUMENT_GENERATION_HISTORY_LABELS.selectStateAidType}
            </Typography>
          )}
          {!isRegenerationTypeSelection && <Box sx={clientStyles.createList}>
            {history?.content.length ? (
              history.content.map((item, index) => {
                const versionLabel = getHistoryVersionLabel(history.content, index);

                return (
                  <Box key={item.id} sx={clientStyles.createListRow}>
                    <Box sx={clientStyles.createListMeta}>
                      <Typography variant="subtitle2" sx={clientStyles.createListTitle}>
                        {getHistoryItemDetails(item, versionLabel)}
                      </Typography>
                      <Typography variant="body2" sx={clientStyles.createListDate}>
                        {DOCUMENT_GENERATION_HISTORY_LABELS.recordPrefix}
                        {item.id}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      disabled={isSelecting || !canProceed}
                      onClick={() => void handleSelect(item.id)}
                      endIcon={<ArrowForwardIcon />}
                      sx={clientStyles.createListBtn}
                    >
                      {DOCUMENT_GENERATION_HISTORY_LABELS.select}
                    </Button>
                  </Box>
                );
              })
            ) : !isLoading ? (
              <Typography variant="body2" sx={clientStyles.createListDate}>
                {DOCUMENT_GENERATION_HISTORY_LABELS.empty}
              </Typography>
            ) : null}
          </Box>}

          {isLoading && (
            <Box sx={commonStyles.loadingMask}>
              <CircularProgress size={28} />
            </Box>
          )}

          {history && history.totalPages > 1 && (
            <Box sx={clientStyles.createPaginationWrap}>
              <TablePaginationControls
                page={page}
                rowsPerPage={DOCUMENT_GENERATION_HISTORY_PAGE_SIZE}
                totalElements={history.totalElements}
                isLoading={isLoading}
                showSummary={false}
                justifyContent="center"
                onPageChange={setPage}
                onRowsPerPageChange={() => {}}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={clientStyles.createActions}>
        <Button onClick={onClose} disabled={isSelecting} sx={clientStyles.createCancelBtn}>
          {DOCUMENT_GENERATION_HISTORY_LABELS.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handlePrimaryAction()}
          disabled={isSelecting || !canProceed}
          sx={clientStyles.createBtn}
        >
          {isRegenerationTypeSelection
            ? DOCUMENT_GENERATION_HISTORY_LABELS.continue
            : DOCUMENT_GENERATION_HISTORY_LABELS.createNew}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
